import { Empty, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { BsListUl } from 'react-icons/bs';
import { IoIosArrowBack } from 'react-icons/io';
import { IoGridOutline } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import useShowUserAlert from '../../../hooks/useShowUserAlert';
import { RootState } from '../../../store/rootReducer';
import { InvestorStatus, dealStatus } from '../../login/utils/Status';
import DealActions from '../deals/redux/actions';
import { Deals as DealsInterface } from '../deals/redux/types';
import ContactUsNew from './components/ContactUsNew';
import DealsView from './components/DealsView';
import DealsFAQ from './components/deals/FAQ';
import DealsTabs, { DealTabType } from './components/deals/Tabs';
import DealsTopCard, { common_container } from './components/deals/TopCard';
import DealsRecentlyClosed from './components/deals/recentlyClose';
import { getType } from './utils';

interface SegregationList {
    active: DealsInterface[];
    upcoming: DealsInterface[];
}
const Deals: React.FC = () => {
    const dispatch = useDispatch();
    const { redirectToSetting } = useShowUserAlert(false);

    const loading = useSelector((state: RootState) => state.deal.loading);
    const deals = useSelector((state: RootState) => state.deal.deals);
    const pastDeals = useSelector((state: RootState) => state.deal.pastDeals);
    const investor = useSelector((state: RootState) => state.user.investor);

    const [activeTab, setActiveTab] = useState<DealTabType>('all');
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const [filter, setFilter] = useState<string>('');

    //Fetch Deals
    useEffect(() => {
        dispatch(DealActions.getDeals());
    }, [dispatch]);

    //Get list by active tab
    const list = useMemo(() => {
        switch (activeTab) {
            case 'all':
                const l = deals.filter(
                    (d) => d.deal.status === dealStatus.active || d.deal.status === dealStatus.upcoming,
                );
                return l;
            case 'growth':
                const g = deals.filter(
                    (d) =>
                        (d.deal.status === dealStatus.active || d.deal.status === dealStatus.upcoming) &&
                        d.deal.info.summary?.dealType === 'Equity',
                );
                return g;
            case 'income':
                const i = deals.filter(
                    (d) =>
                        (d.deal.status === dealStatus.active || d.deal.status === dealStatus.upcoming) &&
                        (d.deal.info.summary?.dealType === 'Debt' || d.deal.info.summary?.dealType === 'Fund'),
                );
                return i;
            case 'closedDeals':
                const cd = pastDeals.filter(
                    (d) => d.deal.status === dealStatus.invested || d.deal.status === dealStatus.completed,
                );
                return cd;
            default:
                const ht = deals.filter(
                    (d) =>
                        d.deal?.dealTags &&
                        d.deal.dealTags.headlinerTags &&
                        d.deal.dealTags.headlinerTags.some((ht) => ht.name === activeTab),
                );
                return ht || [];
        }
    }, [activeTab, deals, pastDeals]);

    const searchFilter = useMemo(() => {
        const convertIntoString = (value?: string | string[] | undefined) => String(value).toLowerCase();

        const checkMatches = (searchKey: string, values?: string | string[] | undefined) =>
            searchKey === '' || convertIntoString(values).includes(key);

        const key = convertIntoString(filter);

        const filteredResults = list.filter((deal: DealsInterface) => {
            const dealType = deal.deal.info.summary?.dealType;
            const modifyDealType = getType(dealType) !== '' ? getType(dealType) : dealType;

            const tagsMatch =
                deal.deal.dealTags?.tags && checkMatches(key, [...deal.deal.dealTags?.tags?.map((t) => t.name)]);
            const titleMatch = checkMatches(key, deal.deal.info.summary?.title);
            const status = checkMatches(key, deal.deal.status);
            const headlinerTagsMatch =
                deal.deal.dealTags?.headlinerTags &&
                checkMatches(key, [...deal.deal.dealTags?.headlinerTags?.map((t) => t.name)]);
            const dealTypeMatch = checkMatches(key, modifyDealType);

            return status || headlinerTagsMatch || tagsMatch || titleMatch || dealTypeMatch;
        });

        return filteredResults;
    }, [list, filter]);

    const segregationList: SegregationList = useMemo(() => {
        return searchFilter.reduce(
            (acc: SegregationList, item: DealsInterface) => {
                if (item.deal.status.toLowerCase() === 'upcoming') {
                    acc.upcoming.push(item);
                } else {
                    acc.active.push(item);
                }
                return acc;
            },
            { active: [], upcoming: [] },
        );
    }, [searchFilter]);

    const descriptionText = () => {
        switch (activeTab) {
            case 'growth':
                return 'We offer curated, unique investment opportunities that have the potential for capital appreciation. Invest in the most innovative firms in the region and explore opportunities generating capital appreciation.';
            case 'income':
                return 'Whether it is through a senior secured debt facility facilitated by Helicap or one of our flagship private credit funds, you can enhance your investment strategy by leveraging stable and recurring revenue streams.';
            default:
                return;
        }
    };

    return (
        <div>
            {/**Top Card */}

            <DealsTopCard />
            {!loading && (
                <div className="bg-white">
                    <div className={`flex justify-between  py-6 ${common_container}`}>
                        {/** Tabs */}
                        {activeTab !== 'closedDeals' ? (
                            <DealsTabs activeTab={activeTab} setActiveTab={(t: DealTabType) => setActiveTab(t)} />
                        ) : (
                            <>
                                <div className="flex items-center">
                                    <IoIosArrowBack
                                        className="mr-2 cursor-pointer"
                                        size="1.4rem"
                                        color="#13974B"
                                        onClick={() => setActiveTab('all')}
                                    />
                                    <p
                                        className="text-lg SourceSansPro-Bold cursor-pointer"
                                        onClick={() => setActiveTab('all')}
                                        style={{ color: '#16365c' }}
                                    >
                                        {'Back'}
                                    </p>
                                </div>
                            </>
                        )}

                        <div className="flex items-center justify-end">
                            <div
                                className={`p-2 ${
                                    view === 'grid' ? 'bg-[#142C53]' : ''
                                } cursor-pointer h-fit hidden md:flex`}
                                onClick={() => setView('grid')}
                            >
                                <IoGridOutline size={'1.4rem'} color={view === 'grid' ? '#fff' : '#7C7B7B'} />
                            </div>
                            <div
                                className={`p-2 ml-2 h-fit ${
                                    view !== 'grid' ? 'bg-[#142C53]' : ''
                                } cursor-pointer hidden md:flex`}
                                onClick={() => setView('list')}
                            >
                                <BsListUl size={'1.4rem'} color={view !== 'grid' ? '#fff' : '#7C7B7B'} />
                            </div>
                        </div>
                    </div>
                    <div className={common_container}>
                        <p className="font-[Lato]">{descriptionText()}</p>
                    </div>
                    <DealsView
                        view={view}
                        dealsList={segregationList.active}
                        title="Active"
                        activeTab={activeTab}
                        isSelectShow={true}
                        // tags={tags}
                        setFilter={setFilter}
                        filter={filter}
                    />
                    {segregationList.upcoming.length ? (
                        <>
                            <DealsView
                                view={view}
                                dealsList={segregationList.upcoming}
                                title="Upcoming"
                                activeTab={activeTab}
                                isSelectShow={false}
                            />
                        </>
                    ) : null}
                    <div className={common_container}>
                        {list.length === 0 && (
                            <div className="mt-4 flex justify-center w-full">
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <p className="font-semibold font-[WorkSans-Semibold] text-lg text-gray-500">
                                            {investor?.status?.accreditation === InvestorStatus.PendingAIReview ||
                                            investor?.status?.accreditation ===
                                                InvestorStatus.PendingAIResubmissionReview ||
                                            investor?.status.kyc === InvestorStatus.PendingKYCReview ||
                                            investor?.status.kyc === InvestorStatus.PendingKYCResubmissionReview ? (
                                                <p className="flex">
                                                    Our team is currently reviewing your information. Thank you for your
                                                    patience.
                                                </p>
                                            ) : (
                                                <p className="flex items-center">
                                                    <span>
                                                        Active deals are only available to Accredited Investors. Our
                                                        team is currently reviewing your information. You will receive a
                                                        notification confirming your accreditation status shortly. Thank
                                                        you for your patience. Meanwhile, you can explore our past deals
                                                        that have been successfully distributed on our platform&nbsp;
                                                        <span
                                                            className="underline cursor-pointer"
                                                            onClick={() => {
                                                                redirectToSetting();
                                                            }}
                                                        >
                                                            Click here
                                                        </span>
                                                        &nbsp;to verify.
                                                    </span>
                                                </p>
                                            )}
                                        </p>
                                    }
                                />
                            </div>
                        )}
                    </div>
                    {/**Recently Closed */}
                    {activeTab !== 'closedDeals' && view.length && (
                        <div>
                            <DealsRecentlyClosed
                                clickOnClosedDeals={() => {
                                    setActiveTab('closedDeals');
                                    window.scroll({
                                        top: 0,
                                        left: 0,
                                    });
                                }}
                                view={view}
                            />
                        </div>
                    )}
                    {/**End Recently Closed */}
                    {/**Contact Us */}
                    <div className="bg-[#0f2240]">
                        <div className={`  ${common_container}`}>
                            {/* <ContactUsDeals /> */}
                            <ContactUsNew isShowTitle={false} />
                        </div>
                    </div>
                    {/** End Contact Us */}
                    {/**FAQ */}
                    <DealsFAQ isViewALl={true} />
                    {/**End FAQ */}
                </div>
            )}
            {loading && (
                <div className="flex justify-center items-center h-72 flex-col">
                    <Spin size="large" />
                    <p className="font-[MartelSans]">Loading.....</p>
                </div>
            )}
        </div>
    );
};

export default Deals;
